import { doc, runTransaction, updateDoc } from 'firebase/firestore';
import { db, usersCollectionRef } from '../firebase';
import {
  AuditActor,
  asFirestoreData,
  logActivity,
  stripSentinels,
} from '../audit-log';
import { diffFields } from '../diff-fields';

const userLabel = (u: { name?: string; ref?: string }): string =>
  u.name || u.ref || '';

// Race-safe user create: the transaction blocks another concurrent create
// from succeeding with the same UID. UI surfaces 'UID_EXISTS' as a friendly
// error instead of allowing two user docs to fight over the same id.
export const createUser = async (
  actor: AuditActor | null,
  uid: string,
  payload: Record<string, unknown>,
): Promise<void> => {
  const userRef = doc(usersCollectionRef, uid);
  await runTransaction(db, async (tx) => {
    const existing = await tx.get(userRef);
    if (existing.exists()) {
      throw new Error('UID_EXISTS');
    }
    tx.set(userRef, payload);
  });
  logActivity({
    actor,
    entity: 'user',
    entityRef: uid,
    entityLabel: userLabel({ ref: uid, name: payload.name as string }),
    action: 'create',
    snapshot: stripSentinels(payload),
  });
};

// Update logging recognises the single-field admin/enable toggles and logs
// them as semantic actions (`grant-admin`, `disable-user`, etc.) instead of
// a generic `update` with a one-key `changes` payload — the activity log
// reads much better that way.
export const updateUser = async (
  actor: AuditActor | null,
  before: {
    ref: string;
    name?: string;
    email?: string;
    enabled?: boolean;
    admin?: boolean;
    canMoveAttendees?: boolean;
  },
  payload: Record<string, unknown>,
): Promise<void> => {
  await updateDoc(
    doc(usersCollectionRef, before.ref),
    asFirestoreData(payload),
  );
  const changes = diffFields(before, payload);
  if (Object.keys(changes).length === 0) return;

  const keys = Object.keys(changes);
  const onlyAdmin = keys.length === 1 && keys[0] === 'admin';
  const onlyEnabled = keys.length === 1 && keys[0] === 'enabled';
  const onlyCanMove =
    keys.length === 1 && keys[0] === 'canMoveAttendees';

  if (onlyAdmin && changes.admin) {
    const toVal = changes.admin.to;
    logActivity({
      actor,
      entity: 'user',
      entityRef: before.ref,
      entityLabel: userLabel(before),
      action: toVal === true ? 'grant-admin' : 'revoke-admin',
    });
    return;
  }
  if (onlyEnabled && changes.enabled) {
    const toVal = changes.enabled.to;
    logActivity({
      actor,
      entity: 'user',
      entityRef: before.ref,
      entityLabel: userLabel(before),
      action: toVal === true ? 'enable-user' : 'disable-user',
    });
    return;
  }
  if (onlyCanMove && changes.canMoveAttendees) {
    const toVal = changes.canMoveAttendees.to;
    logActivity({
      actor,
      entity: 'user',
      entityRef: before.ref,
      entityLabel: userLabel(before),
      action:
        toVal === true ? 'grant-move-attendees' : 'revoke-move-attendees',
    });
    return;
  }

  logActivity({
    actor,
    entity: 'user',
    entityRef: before.ref,
    entityLabel: userLabel(before),
    action: 'update',
    changes,
  });
};
